/**
 * Super Admin Platform Overview.
 *
 * Cards labeled "All brands aggregate". Per-brand bar chart sourced
 * from /api/admin/brands (we have active_customers + branch_count per
 * brand). Recent platform audit events from /api/admin/audit-logs.
 *
 * NOTE: backend doesn't currently expose per-brand revenue aggregation
 * in admin/overview — we render `active customers per brand` instead
 * and clearly label it. Revenue split per brand would require a new
 * aggregation endpoint (out of scope here).
 */
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import {
  Building2, Users, Receipt, Wallet, Gift, Stamp as StampIcon, UserCog,
  ScrollText,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import { formatRupiah, formatDateTime } from '@/lib/utils';
import {
  PageHeader, SectionCard, FullSpinner, QueryError, StatusPill,
} from '@/components/ui_primitives/Misc';
import KpiCard from '@/components/ui_primitives/KpiCard';
import { JsonViewer } from '@/components/ui_primitives/Misc';
import { useState } from 'react';

export default function AdminOverviewPage() {
  const token = useAuthStore((s) => s.token);
  const overviewQ = useQuery({
    queryKey: qk.adminOverview(),
    queryFn: () => api.adminOverview(token),
    enabled: !!token,
  });
  const brandsQ = useQuery({
    queryKey: qk.adminBrands(),
    queryFn: () => api.adminBrands(token),
    enabled: !!token,
  });
  const auditQ = useQuery({
    queryKey: qk.adminAudit({ limit: 20 }),
    queryFn: () => api.adminAuditLogs(token, { limit: 20 }),
    enabled: !!token,
  });

  if (overviewQ.isLoading) return <FullSpinner />;
  if (overviewQ.error) return <QueryError error={overviewQ.error} onRetry={() => overviewQ.refetch()} />;

  const o = overviewQ.data || {};
  const brandsRanked = (brandsQ.data || [])
    .slice()
    .sort((a, b) => (b.active_customers ?? 0) - (a.active_customers ?? 0))
    .slice(0, 8);

  return (
    <div className="space-y-8" data-testid="admin-overview-page">
      <PageHeader
        kicker="Platform Overview"
        title="Platform Console"
        subtitle="All-brands aggregate. Drill into a brand from below for brand-specific health."
        actions={
          <Link to="/admin/brands">
            <StatusPill variant="gold">{(o.total_active_brands ?? 0)} active brands</StatusPill>
          </Link>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <KpiCard label="All brands aggregate · Brands" value={(o.total_brands ?? 0).toLocaleString('id-ID')}
          sub={`${o.total_active_brands ?? 0} active`} icon={<Building2 size={14} />} testID="kpi-brands" />
        <KpiCard label="All brands aggregate · Customers" value={(o.total_customers ?? 0).toLocaleString('id-ID')}
          icon={<Users size={14} />} testID="kpi-customers" />
        <KpiCard label="All brands aggregate · Transactions" value={(o.total_transactions ?? 0).toLocaleString('id-ID')}
          icon={<Receipt size={14} />} testID="kpi-tx" />
        <KpiCard label="All brands aggregate · Revenue" value={formatRupiah(o.total_revenue_rupiah)}
          icon={<Wallet size={14} />} testID="kpi-rev" />
        <KpiCard label="All brands aggregate · Stamps issued" value={(o.total_stamps_issued ?? 0).toLocaleString('id-ID')}
          icon={<StampIcon size={14} />} testID="kpi-stamps" />
        <KpiCard label="All brands aggregate · Rewards redeemed" value={(o.total_rewards_redeemed ?? 0).toLocaleString('id-ID')}
          icon={<Gift size={14} />} testID="kpi-redeem" />
        <KpiCard label="Platform · Cashiers" value={(o.total_cashiers ?? 0).toLocaleString('id-ID')}
          icon={<UserCog size={14} />} testID="kpi-cashiers" />
        <KpiCard label="Platform · Brand owners" value={(o.total_owners ?? 0).toLocaleString('id-ID')}
          icon={<UserCog size={14} />} testID="kpi-owners" />
      </div>

      <SectionCard testID="admin-brands-chart">
        <h3 className="font-display text-lg font-semibold mb-1">Brands ranked by active customers</h3>
        <p className="text-xs text-white/50 mb-4">Top {brandsRanked.length} by unique customers who joined the brand's card.</p>
        {brandsRanked.length === 0 ? (
          <div className="text-sm text-white/40 py-12 text-center">No brand activity yet.</div>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={brandsRanked} margin={{ left: -10, right: 16, top: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: '#8A8A8A', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#8A8A8A', fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: '#0E0E13', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 }}
                />
                <Bar dataKey="active_customers" radius={[6, 6, 0, 0]} fill="#C9A84C" isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </SectionCard>

      <SectionCard testID="admin-recent-audit">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display text-lg font-semibold flex items-center gap-2"><ScrollText size={16} className="text-gold" />Recent platform audit</h3>
            <p className="text-xs text-white/50 mt-1">Last 20 events across all brands.</p>
          </div>
          <Link to="/admin/audit" className="text-xs font-semibold text-gold hover:underline">View all →</Link>
        </div>
        {auditQ.isLoading ? <FullSpinner /> : null}
        {(() => {
          const items = Array.isArray(auditQ.data) ? auditQ.data : (auditQ.data?.items || []);
          if (!auditQ.isLoading && items.length === 0) {
            return <div className="text-sm text-white/40 py-12 text-center">No audit events yet.</div>;
          }
          return <AuditList items={items} />;
        })()}
      </SectionCard>
    </div>
  );
}

function AuditList({ items }) {
  const [expanded, setExpanded] = useState(null);
  if (!items.length) return null;
  return (
    <ul className="divide-y divide-white/5">
      {items.map((row) => {
        const isOpen = expanded === row.id;
        return (
          <li key={row.id} className="py-3" data-testid="admin-audit-row">
            <button type="button" onClick={() => setExpanded(isOpen ? null : row.id)}
              className="w-full text-left flex items-start gap-3 hover:bg-white/3 rounded-lg px-2 py-1.5 transition">
              <div className="w-8 h-8 rounded-lg bg-gold/15 border border-gold/30 grid place-items-center text-gold text-[10px] shrink-0">
                {row.action.split('_').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-white">
                  <span className="font-mono text-xs text-gold">{row.action}</span>
                  {row.target_type ? <span className="text-white/40"> · {row.target_type}</span> : null}
                </div>
                <div className="text-[11px] text-white/40 mt-0.5">
                  {row.actor_email ? `${row.actor_email} · ` : ''}{formatDateTime(row.created_at)}
                </div>
              </div>
            </button>
            {isOpen ? <div className="ml-11 mt-2"><JsonViewer data={row.metadata || {}} /></div> : null}
          </li>
        );
      })}
    </ul>
  );
}
